import {
  Box,
  Flex,
  HStack,
  Link,
  Stack,
  useMultiStyleConfig,
  VStack,
} from '@chakra-ui/react';
import { RenderRichTextData } from 'gatsby-source-contentful/rich-text';
import React from 'react';

import { FooterFragment } from '../../../graphql-types';
import {
  FacebookIcon,
  InstagramIcon,
  WLContainer,
  WLH2,
  WLImageFader,
  WLLink,
  XIcon,
  YouTubeIcon,
} from '../../design-library';
import ContentfulUtils from '../../utils/contentful.utils';
import { ToggleInfoDisplay } from '../../utils/onetrust.utils';
import ImageAdapter from '../ImageAdapter';
import LinksArrayAdapter from '../LinksArrayAdapter';
import RichText from '../RichText';

interface FooterProps {
  data: FooterFragment;
  isCareersPage: boolean;
}

const Footer: React.FC<FooterProps> = ({ data, isCareersPage }) => {
  const styles = useMultiStyleConfig('Footer', {});
  if (!data) {
    return null;
  }
  return (
    <WLContainer width="FULL_WIDTH" sx={styles.footerContainer}>
      {/* Primary Section */}
      <Box sx={styles.footerPrimaryContainer}>
        <Box sx={styles.footerLogoContainer}>
          <svg
            id="Logo"
            width="auto"
            height="44px"
            xmlns="http://www.w3.org/2000/svg"
          >
            <g id="Group_370" data-name="Group 370">
              <path
                id="Subtraction_3"
                data-name="Subtraction 3"
                d="M21.089,42.181A21.1,21.1,0,0,1,12.88,1.657,21.1,21.1,0,0,1,29.3,40.523,20.951,20.951,0,0,1,21.089,42.181ZM23.5,23.753v16.51a18.184,18.184,0,0,0,3.492-.743V24.936L23.5,23.753Zm-4.752-.074-3.488,1.183V39.373a17.927,17.927,0,0,0,3.488.743V23.679Zm-5.864,2.073L9.394,26.935v9.478a21.379,21.379,0,0,0,3.492,2.147V25.752Zm16.41,0V38.56a18.667,18.667,0,0,0,3.492-2.147V26.935L29.3,25.752Zm5.864,2.073v6.441a20.862,20.862,0,0,0,3.492-5.258L35.16,27.825ZM7.018,27.751,3.529,28.934a20.821,20.821,0,0,0,3.488,5.258V27.751Zm0-4.589-4.826,1.7.595,2.373,4.231-1.482v-2.59Zm22.278-2v2.59l10.1,3.481.595-2.373Zm-14.034-.891L9.394,22.345v2.59l5.868-2V20.272Zm5.864.591h0l5.868,2.073V20.272l-5.868-2-3.488,1.187v2.664l3.488-1.26Zm.017-18.947A19.38,19.38,0,0,0,7.476,7.56a19.246,19.246,0,0,0-5.137,9.176l18.787-6.515h0l18.79,6.515a19.158,19.158,0,0,0-6.9-10.73A19.426,19.426,0,0,0,21.143,1.917Z"
                fill="#fff"
              />
            </g>
            <g
              id="Group_371"
              data-name="Group 371"
              transform="translate(46.54 9.861)"
            >
              <path
                id="Path_2"
                data-name="Path 2"
                d="M59.9,11.868h2.739l3.332,16.806h.074l3.7-16.806h3.332l3.7,16.806h.074l3.628-16.806H83L78.483,32.6H75.373L71.45,14.829h-.074L67.452,32.6H64.342L59.9,11.868m28.208,5.4c4,0,6.071,3.035,6.071,7.774s-2.073,7.774-6.071,7.774-6.071-3.035-6.071-7.774,2.073-7.774,6.071-7.774m0,13.549c2.369,0,3.48-1.925,3.48-5.775,0-3.776-1.111-5.775-3.48-5.775s-3.48,1.925-3.48,5.775c0,3.776,1.111,5.775,3.48,5.775m13.7-13.549c4,0,6.071,3.035,6.071,7.774S105.8,32.82,101.8,32.82s-6.071-3.035-6.071-7.774,2.073-7.774,6.071-7.774m0,13.549c2.369,0,3.48-1.925,3.48-5.775,0-3.776-1.111-5.775-3.48-5.775s-3.48,1.925-3.48,5.775c0,3.776,1.111,5.775,3.48,5.775M121.2,32.6h-2.369l-.074-2.221h-.074a4.315,4.315,0,0,1-4,2.443c-3.109,0-5.257-2.591-5.257-7.774,0-5.257,2.073-7.774,5.257-7.774a4.1,4.1,0,0,1,3.85,2.3h.074V10.313h2.517V29.784Zm-5.923-13.326c-2.3,0-3.332,1.925-3.332,5.775,0,3.776,1.037,5.775,3.332,5.775s3.332-1.925,3.332-5.775c0-3.776-1.036-5.775-3.332-5.775m7.848-1.777H125.5l.074,2.221h.074a3.882,3.882,0,0,1,3.776-2.443,3.737,3.737,0,0,1,3.554,2.369,3.977,3.977,0,0,1,3.7-2.369c2.591,0,4.368,1.481,4.368,4.812V32.6h-2.517V22.233c0-1.925-.814-2.813-2.3-2.813-1.851,0-2.887,1.111-2.887,3.85V32.6h-2.517V22.233c0-1.925-.814-2.813-2.3-2.813-1.851,0-2.887,1.111-2.887,3.85V32.6h-2.517v-15.1Zm22.285,8.292c0,3.48,1.333,5.108,4.146,5.108a6.2,6.2,0,0,0,3.48-1.259l.148,2.221a9.79,9.79,0,0,1-4.22.888c-4,0-6.071-3.035-6.071-7.774s2.073-7.774,5.849-7.774c3.406,0,5.331,2.739,5.331,7.181v1.333h-8.662Zm6.071-1.925c0-3.184-.962-4.738-2.961-4.738-1.629,0-3.035,1.185-3.109,4.738ZM156,17.494h2.369l.074,2.221h.074a3.924,3.924,0,0,1,3.85-2.443c2.665,0,4.442,1.481,4.442,4.812V32.6H164.29V22.825c0-2.369-.888-3.48-2.517-3.48-1.925,0-3.184,1.333-3.184,4.072v9.106h-2.517V20.752Z"
                transform="translate(-59.9 -10.313)"
                fill="#fff"
              />
              <path
                id="Path_11"
                data-name="Path 11"
                d="M172.011,11.8h3.7V28.98h6.515v3.628H172.011m14.141-17.1a2.221,2.221,0,1,1,2.221-2.221,2.221,2.221,0,0,1-2.221,2.221m-1.777,17.1V17.06h3.48V32.681h-3.48Zm12.66-18.361c-.962,0-1.333.37-1.333,1.481v1.259h2.517v3.332H195.7v12.29h-3.48V20.391h-1.555V17.06h1.555V15.209c0-2.887,1.7-4.072,3.776-4.072a6.66,6.66,0,0,1,2.665.444v3.035A2.963,2.963,0,0,0,197.035,14.246Zm13.993,11.92H203.18c.148,2.369,1.111,3.48,2.517,3.48a2.626,2.626,0,0,0,2.443-1.333l2.517,1.851a5.628,5.628,0,0,1-4.96,2.813c-3.85,0-6-3.332-6-8.07,0-5.257,2.369-8.144,5.775-8.144,3.924,0,5.7,3.85,5.7,8.144ZM205.4,19.873c-1.333,0-2,1.111-2.221,3.406h4.442c-.222-1.925-.74-3.406-2.221-3.406m7.4.888a1.851,1.851,0,0,1-1.925-1.774q0-.076,0-.151a1.826,1.826,0,0,1,1.925-1.925,1.925,1.925,0,1,1,0,3.85m0-3.628a1.7,1.7,0,1,0,1.7,1.7,1.674,1.674,0,0,0-1.7-1.7m.37,1.777.444.888h-.37l-.444-.888h-.444V19.8h-.3V17.652h.888a.6.6,0,0,1,.666.592.6.6,0,0,1-.444.666m-.222-.962h-.592v.74h.518c.3,0,.444-.148.444-.37C213.4,18.1,213.175,17.948,212.953,17.948Z"
                transform="translate(-61.328 -10.323)"
                fill="#fff"
              />
            </g>
          </svg>
        </Box>
        <Flex direction={['column', null, 'row']} sx={styles.footerPrimary}>
          <Box flexShrink={1} flexGrow={0} flexBasis="33.33%">
            <Box sx={styles.footerPrimarySiteLinks}>
              <LinksArrayAdapter json={data?.footerLinks?.internal?.content} />
            </Box>
            <Box sx={styles.footerPrimarySocialLinks}>
              {(data.facebookLink ||
                data.instagramLink ||
                data.twitterLink ||
                data.youTubeLink) && (
                <HStack>
                  {data.youTubeLink && (
                    <WLLink aria-label="YouTube Link" to={data.youTubeLink}>
                      <YouTubeIcon boxSize="3rem" />
                    </WLLink>
                  )}
                  {data.facebookLink && (
                    <WLLink aria-label="Facebook Link" to={data.facebookLink}>
                      <FacebookIcon boxSize="3rem" />
                    </WLLink>
                  )}
                  {data.twitterLink && (
                    <WLLink aria-label="Twitter Link" to={data.twitterLink}>
                      <XIcon boxSize="3rem" />
                    </WLLink>
                  )}
                  {data.instagramLink && (
                    <WLLink aria-label="Instagram Link" to={data.instagramLink}>
                      <InstagramIcon boxSize="3rem" />
                    </WLLink>
                  )}
                </HStack>
              )}
            </Box>
            <Box sx={styles.footerEmailSignUp}>
              <RichText
                value={data?.footerEmailSignUp as RenderRichTextData<never>}
              />
            </Box>
          </Box>
          <Box
            flexShrink={1}
            flexGrow={0}
            flexBasis={'33.33%'}
            sx={styles.footerPrimaryContact}
            mb={6}
          >
            <RichText
              value={
                data?.footerCustomerServiceContent as RenderRichTextData<never>
              }
            />
          </Box>
          <Box
            flexShrink={1}
            flexGrow={0}
            flexBasis="33.33%"
            sx={styles.footerPrimaryLegalLinks}
          >
            <Link
              id="ot-sdk-btn"
              className="ot-sdk-show-settings"
              onClick={() => ToggleInfoDisplay()}
            >
              Do Not Sell or Share My Information
            </Link>
            <RichText
              value={data?.footerLegalLinks as RenderRichTextData<never>}
            />
          </Box>
        </Flex>
      </Box>
      {/* Secondary Section */}
      <Box sx={styles.footerSecondary}>
        {/* Mission Statement Section */}
        <Box sx={styles.footerMission}>
          <Box sx={styles.footerMissionGradientLeft}></Box>
          <Box sx={styles.footerMissionText}>
            <RichText
              value={data?.footerMissionStatement as RenderRichTextData<never>}
            />
          </Box>
          <Box sx={styles.footerMissionGradientRight}></Box>
        </Box>
        {/* Disclosures Section */}
        <Stack
          direction={['column', null, 'row']}
          sx={styles.footerDisclosuresSection}
        >
          <Stack order={['2', '2', '1']} sx={styles.footerDisclosures}>
            {isCareersPage && (
              <RichText
                value={data?.footerCareersContent as RenderRichTextData<never>}
              />
            )}
            <RichText
              value={data?.footerMainContent as RenderRichTextData<never>}
            />
          </Stack>
          <VStack
            order={['1', '1', '2']}
            sx={styles.footerRatingsAndAwardsContainer}
          >
            <VStack sx={styles.footerRatingsAndAwards}>
              {data?.footerRatingsAndAwardsImages?.length && (
                <VStack>
                  <HStack sx={{ paddingBottom: '24px' }}>
                    <WLH2>Ratings &amp; Awards</WLH2>
                    <WLLink to="/about/awards-and-ratings/">View All</WLLink>
                  </HStack>
                  <WLImageFader
                    images={(data?.footerRatingsAndAwardsImages || []).map(
                      (image, index) => (
                        <ImageAdapter
                          key={index}
                          data={ContentfulUtils.getAssetInfo(image)}
                        />
                      ),
                    )}
                  />
                </VStack>
              )}
              <Box sx={styles.footerFraternalAlliance}>
                <RichText
                  value={
                    data?.footerFraternalAlliance as RenderRichTextData<never>
                  }
                />
              </Box>
            </VStack>
          </VStack>
        </Stack>
      </Box>
      {/* Footer - Copyright Section */}
      <Box sx={styles.footerCopyrightContainer}>
        <Box sx={styles.footerCopyright}>
          <RichText
            value={data?.footerCopyright as RenderRichTextData<never>}
          />
        </Box>
      </Box>
    </WLContainer>
  );
};

export default Footer;
